import React from 'react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Tent, 
  Store, 
  BadgeCheck, 
  Zap, 
  MessageSquare, 
  Play, 
  Search, 
  FileText, 
  Map as MapIcon, 
  CheckCircle2,
  Calendar,
  Users
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';

const Step = ({ number, title, description, delay = 0 }: { number: string, title: string, description: string, delay?: number }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.5 }}
    className="relative pl-12 pb-12 last:pb-0"
  >
    <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#FF5C00] to-[#FF8A00] flex items-center justify-center text-white font-black text-sm shadow-[0_0_15px_rgba(255,92,0,0.3)]">
      {number}
    </div>
    <div className="absolute left-4 top-8 bottom-0 w-[1px] bg-gradient-to-b from-[#FF5C00]/50 to-transparent last:hidden" />
    <h3 className="text-xl font-bold mb-2 text-white">{title}</h3>
    <p className="text-gray-400 leading-relaxed">{description}</p>
  </motion.div>
);

const VideoSection = ({ title, subtitle, videoId, color = "#FF5C00" }: { title: string, subtitle: string, videoId: string, color?: string }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.95 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    className="relative rounded-3xl overflow-hidden aspect-video bg-[#121212] border border-white/5 group shadow-2xl"
  >
    {/* Real Video Embed or Placeholder */}
    <iframe 
      className="absolute inset-0 w-full h-full opacity-60 group-hover:opacity-80 transition-opacity"
      src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0`}
      title="Background video"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    />
    
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
    
    <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
      <div 
        className="w-16 h-16 rounded-full flex items-center justify-center mb-6 cursor-pointer transform transition-transform group-hover:scale-110"
        style={{ backgroundColor: color }}
      >
        <Play className="w-8 h-8 text-white fill-current ml-1" />
      </div>
      <h3 className="text-2xl md:text-3xl font-black mb-2 text-white drop-shadow-lg">{title}</h3>
      <p className="text-gray-300 max-w-md drop-shadow-md">{subtitle}</p>
    </div>
  </motion.div>
);

export default function HowItWorksPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/50 backdrop-blur-xl border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home')} className="p-2 hover:bg-white/5 rounded-full transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Logo />
          </div>
          <button 
            onClick={() => navigate('/home')}
            className="bg-white/5 hover:bg-white/10 text-white font-bold py-2 px-6 rounded-xl transition-all border border-white/10 text-sm"
          >
            Se connecter
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[#FF5C00] text-xs font-black uppercase tracking-[0.2em] mb-8"
          >
            Guide Complet
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl lg:text-7xl font-black mb-8 leading-tight tracking-tight"
          >
            Comment fonctionne <br />
            la plateforme <span className="text-[#FF5C00]">FESTIN</span> ?
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed"
          >
            FESTIN simplifie la rencontre entre les organisateurs d'événements et les stands professionnels. 
            Une solution tout-en-un pour gérer candidatures, logistique et communication.
          </motion.p>
        </div>
      </section>

      {/* Part 1: For Festivals */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent to-[#4b3cfa]/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-[#4b3cfa]/20 flex items-center justify-center text-[#4b3cfa]">
                  <Tent className="w-6 h-6" />
                </div>
                <h2 className="text-3xl font-black uppercase tracking-tight">Pour les Festivals</h2>
              </div>
              
              <div className="mt-12 space-y-2">
                <Step 
                  number="1" 
                  title="Créez votre événement" 
                  description="En quelques minutes, décrivez votre festival, les dates, le lieu et les types de stands que vous recherchez (food, artisanat, bar, etc.)."
                  delay={0.1}
                />
                <Step 
                  number="2" 
                  title="Publiez vos besoins" 
                  description="Définissez les contraintes techniques : emplacements, accès électricité, eau, et tarifs de location."
                  delay={0.2}
                />
                <Step 
                  number="3" 
                  title="Gérez les candidatures" 
                  description="Recevez et examinez les dossiers des stands. Sélectionnez les meilleurs candidats en un clic."
                  delay={0.3}
                />
                <Step 
                  number="4" 
                  title="Planifiez votre terrain" 
                  description="Utilisez notre outil carte pour placer vos stands et organiser la logistique technique de votre festival."
                  delay={0.4}
                />
              </div>
            </div>
            
            <div>
              <VideoSection 
                title="Démo Festival" 
                subtitle="Découvrez comment gérer plus de 100 stands sans stress."
                videoId="qjP4QdZK7zc"
                color="#4b3cfa"
              />
              
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-white/5 border border-white/5 p-6 rounded-3xl">
                  <BadgeCheck className="text-[#4b3cfa] mb-4" />
                  <h4 className="font-bold mb-1">Sécurité</h4>
                  <p className="text-xs text-gray-400">Dossiers administratifs centralisés et vérifiés.</p>
                </div>
                <div className="bg-white/5 border border-white/5 p-6 rounded-3xl">
                  <Zap className="text-yellow-400 mb-4" />
                  <h4 className="font-bold mb-1">Gain de temps</h4>
                  <p className="text-xs text-gray-400">Automatisez la réception des règlements et contrats.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transition: Feature Grid */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-black mb-6">Des outils puissants pour tous</h2>
            <p className="text-gray-400">Une interface fluide pensée pour le terrain.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: MessageSquare, title: "Messagerie Directe", desc: "Communiquez instantanément avec vos partenaires via notre chat sécurisé." },
              { icon: MapIcon, title: "Outil de Mapping", desc: "Placez vos stands sur le plan et gérez les branchements techniques." },
              { icon: FileText, title: "Dossiers Cloud", desc: "Tous vos documents (Kbis, assurance, etc.) sont stockés et accessibles partout." }
            ].map((feature, i) => (
              <motion.div 
                key={i}
                whileHover={{ y: -10 }}
                className="bg-[#121212] border border-white/5 p-10 rounded-[32px] text-center"
              >
                <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="w-8 h-8 text-[#FF5C00]" />
                </div>
                <h3 className="text-xl font-bold mb-4">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Part 2: For Stands */}
      <section className="py-20 px-6 bg-gradient-to-t from-transparent to-[#FF5C00]/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <VideoSection 
                title="Démo Stand" 
                subtitle="Optimisez votre saison et boostez votre chiffre d'affaires."
                videoId="jP1m7v7_Y68"
                color="#FF5C00"
              />
              
              <div className="space-y-4 mt-8">
                <div className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/5">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-500">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm">Visibilité maximale</h4>
                    <p className="text-xs text-gray-500">Votre profil est vu par des centaines d'organisateurs.</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/5">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-500">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm">Gestion du calendrier</h4>
                    <p className="text-xs text-gray-500">Suivez vos acceptations et planifiez vos déplacements.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-[#FF5C00]/20 flex items-center justify-center text-[#FF5C00]">
                  <Store className="w-6 h-6" />
                </div>
                <h2 className="text-3xl font-black uppercase tracking-tight">Pour les Stands</h2>
              </div>
              
              <div className="mt-12 space-y-2">
                <Step 
                  number="1" 
                  title="Créez votre profil vitrine" 
                  description="Mettez en avant vos produits, vos photos de stand et vos références passées."
                  delay={0.1}
                />
                <Step 
                  number="2" 
                  title="Explorez les festivals" 
                  description="Filtrez par date, par région ou par type d'événement pour trouver les meilleures opportunités."
                  delay={0.2}
                />
                <Step 
                  number="3" 
                  title="Postulez en un clic" 
                  description="Plus besoin de remplir 10 fois le même formulaire. Vos infos sont transférées automatiquement."
                  delay={0.3}
                />
                <Step 
                  number="4" 
                  title="Suivez vos contrats" 
                  description="Tous vos échanges et validations sont centralisés. Préparez votre logistique sereinement."
                  delay={0.4}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-40 px-6">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-[#4b3cfa] to-[#FF5C00] rounded-[48px] p-12 md:p-20 text-center relative overflow-hidden">
          {/* Abstract Decorations */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/20 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="relative z-10">
            <h2 className="text-4xl md:text-6xl font-black mb-8 text-white tracking-tight">
              Prêt à rejoindre <br /> l'aventure ?
            </h2>
            <p className="text-white/80 text-lg mb-12 max-w-xl mx-auto">
              Inscrivez-vous dès maintenant et découvrez le futur de l'organisation événementielle par vous-même.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={() => navigate('/home')}
                className="w-full sm:w-auto bg-white text-black font-black py-4 px-10 rounded-2xl hover:scale-105 transition-transform"
              >
                Lancer mon projet
              </button>
              <button 
                onClick={() => navigate('/home')}
                className="w-full sm:w-auto bg-black/20 backdrop-blur-md text-white font-black py-4 px-10 rounded-2xl border border-white/20 hover:bg-black/30 transition-all"
              >
                Explorer la carte
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
